/**
 * WorkflowSession.tsx — /workflow 页顶层容器
 *
 * 职责：
 *     WorkflowChrome + scoped AgentLoopView；workflow SSE Source 写入 workflow store。
 *
 * 链路位置：
 *     Simple /workflow 页 · 业务 Workflow 演示。
 *
 * 当前裁剪范围：
 *     无 WorkflowTaskListFooter（P3）；permission 与 AgentSession 同构 props 子集。
 */
import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import { WorkflowSessionStoreContext } from '../../core/context/WorkflowSessionContext';
import { WorkflowLoopReplayProvider } from '../../core/context/WorkflowLoopReplayContext';
import type { WorkflowLoopReplayHandler } from '../../core/context/WorkflowLoopReplayContext';
import {
  WorkflowScaleContext,
} from '../../core/context/WorkflowScaleContext';
import {
  DEFAULT_WORKFLOW_SCALE_OPTIONS,
  type WorkflowScaleOptions,
} from './workflowScaleOptions';
import {
  NodeRegistryContext,
  ToolDisplayOptionsContext,
  SessionViewOptionsContext,
} from '../../core/context/SessionContext';
import { InteractionBusContext } from '../../core/context/InteractionContext';
import { MarkdownRendererContext } from '../../core/context/MarkdownRendererContext';
import type { MarkdownRenderer } from '../../core/context/MarkdownRendererContext';
import { getNoopInteractionBus } from '../../core/interaction/InteractionBus';
import type { InteractionBus } from '../../core/interaction/InteractionBus';
import { createWorkflowSessionStore } from '../../core/workflowSessionStore';
import { reduceWorkflowEvents } from '../../core/workflowReducer';
import type { Source } from '../../transport/types';
import { SseTransportTerminalError } from '../../transport/createAgentxSseSource';
import type { LangchainAgentEvent, TierOverrides } from '../../types/events';
import { createDefaultRegistry } from '../defaultRegistry';
import type { NodeRegistry } from '../NodeRegistry';
import {
  createDefaultToolRegistry,
  type ToolDisplayRegistry,
} from '../tools/registry';
import type { ToolBodyMode } from '../tools/types';
import type { DisplayMode, PermissionUiMode } from '../sessionViewOptions';
import { DEFAULT_SESSION_VIEW_OPTIONS } from '../sessionViewOptions';
import { WorkflowChrome } from './WorkflowChrome';
import { WorkflowTaskListFooter } from './WorkflowTaskListFooter';
import { useStore } from 'zustand';
import type { WorkflowSessionStoreState } from '../../core/workflowSessionStore';

export interface WorkflowSessionProps {
  source: Source;
  initialEvents?: LangchainAgentEvent[];
  registry?: NodeRegistry;
  tierOverrides?: TierOverrides;
  onError?: (error: Error) => void;
  interactionBus?: InteractionBus;
  toolDisplayRegistry?: ToolDisplayRegistry;
  defaultBodyMode?: ToolBodyMode;
  permissionUiMode?: PermissionUiMode;
  markdownRenderer?: MarkdownRenderer;
  displayMode?: DisplayMode;
  verbose?: boolean;
  exploreFullscreenBash?: boolean;
  memoryDir?: string | null;
  workspaceRoot?: string | null;
  virtualized?: boolean;
  virtualizeThreshold?: number;
  groupParallelTools?: boolean;
  /** P1–P4：大规模 workflow 缩放选项 */
  workflowScale?: Partial<WorkflowScaleOptions>;
  /** P2：展开已淘汰 loop 时由宿主 replay 事件 */
  onRequestLoopReplay?: WorkflowLoopReplayHandler;
  /** SSE 流正常结束（传输 resolve）；与 status=done 互补，供宿主解锁 loading */
  onStreamComplete?: () => void;
  children?: ReactNode;
}

export function WorkflowSession({
  source,
  initialEvents,
  registry,
  tierOverrides,
  onError,
  interactionBus,
  toolDisplayRegistry,
  defaultBodyMode = 'preview',
  permissionUiMode = 'standalone',
  markdownRenderer,
  displayMode = 'normal',
  verbose = false,
  exploreFullscreenBash = true,
  memoryDir = null,
  workspaceRoot = null,
  virtualized = false,
  virtualizeThreshold,
  groupParallelTools = false,
  workflowScale,
  onRequestLoopReplay,
  onStreamComplete,
  children,
}: WorkflowSessionProps) {
  const storeRef = useRef<ReturnType<typeof createWorkflowSessionStore> | null>(null);
  if (storeRef.current === null) {
    const initialState =
      initialEvents && initialEvents.length > 0
        ? reduceWorkflowEvents(initialEvents, { tierOverrides })
        : undefined;
    storeRef.current = createWorkflowSessionStore(initialState, {
      tierOverrides,
      initialEventCount: initialEvents?.length ?? 0,
      evictCompletedLoops: workflowScale?.evictCompletedLoops
        ?? DEFAULT_WORKFLOW_SCALE_OPTIONS.evictCompletedLoops,
      maxHydratedCompletedLoops: workflowScale?.maxHydratedCompletedLoops
        ?? DEFAULT_WORKFLOW_SCALE_OPTIONS.maxHydratedCompletedLoops,
    });
  }

  const scaleOptions = useMemo(
    () => ({ ...DEFAULT_WORKFLOW_SCALE_OPTIONS, ...workflowScale }),
    [workflowScale],
  );

  const registryRef = useMemo(() => registry ?? createDefaultRegistry(), [registry]);
  const busRef = useMemo(
    () => interactionBus ?? getNoopInteractionBus(),
    [interactionBus],
  );
  const toolDisplayRef = useMemo(
    () => ({
      registry: toolDisplayRegistry ?? createDefaultToolRegistry(),
      defaultBodyMode,
    }),
    [toolDisplayRegistry, defaultBodyMode],
  );
  const sessionViewRef = useMemo(
    () => ({
      ...DEFAULT_SESSION_VIEW_OPTIONS,
      groupParallelTools,
      permissionUiMode,
      displayMode,
      verbose,
      exploreFullscreenBash,
      memoryDir,
      workspaceRoot,
    }),
    [
      groupParallelTools,
      permissionUiMode,
      displayMode,
      verbose,
      exploreFullscreenBash,
      memoryDir,
      workspaceRoot,
    ],
  );

  const containerTree = useStore(
    storeRef.current!,
    (s: WorkflowSessionStoreState) => s.state.containerTree,
  );
  const loopTreesBySessionId = useStore(
    storeRef.current!,
    (s: WorkflowSessionStoreState) => s.state.loopTreesBySessionId,
  );
  const sessionStatus = useStore(
    storeRef.current!,
    (s: WorkflowSessionStoreState) => s.state.status,
  );
  const internalErrors = useStore(
    storeRef.current!,
    (s: WorkflowSessionStoreState) => s.state.internalErrors,
  );

  const onErrorRef = useRef(onError);
  const onStreamCompleteRef = useRef(onStreamComplete);
  onErrorRef.current = onError;
  onStreamCompleteRef.current = onStreamComplete;

  useEffect(() => {
    const store = storeRef.current!;
    const controller = new AbortController();

    void source.start((event, ctx) => {
      store.getState().applyEvent(event, ctx);
    }, controller.signal)
      .then(() => {
        store.getState().markStreamComplete();
        onStreamCompleteRef.current?.();
      })
      .catch((err: unknown) => {
        if (err instanceof SseTransportTerminalError) {
          store.getState().markAsError(err);
          onErrorRef.current?.(err);
        } else if (err instanceof Error) {
          onErrorRef.current?.(err);
        }
      });

    return () => {
      controller.abort();
    };
  }, [source]);

  return (
    <WorkflowSessionStoreContext.Provider value={storeRef.current!}>
      <WorkflowScaleContext.Provider value={scaleOptions}>
        <WorkflowLoopReplayProvider onRequestLoopReplay={onRequestLoopReplay}>
          <InteractionBusContext.Provider value={busRef}>
            <NodeRegistryContext.Provider value={registryRef}>
              <MarkdownRendererContext.Provider value={markdownRenderer}>
                <ToolDisplayOptionsContext.Provider value={toolDisplayRef}>
                  <SessionViewOptionsContext.Provider value={sessionViewRef}>
                    <div
                      className="lax-agent-session lax-workflow-session"
                      data-testid="lax-workflow-session"
                    >
                      {(sessionStatus === 'error' || internalErrors.length > 0) ? (
                        <div
                          className="lax-workflow-error"
                          data-testid="lax-workflow-error"
                          role="alert"
                        >
                          {internalErrors[internalErrors.length - 1]?.message
                            ?? 'Workflow stream error'}
                        </div>
                      ) : null}
                      <WorkflowChrome
                        containerTree={containerTree}
                        loopTreesBySessionId={loopTreesBySessionId}
                        virtualized={virtualized}
                        virtualizeThreshold={virtualizeThreshold}
                        groupParallelTools={groupParallelTools}
                      />
                      <WorkflowTaskListFooter />
                      {children}
                    </div>
                  </SessionViewOptionsContext.Provider>
                </ToolDisplayOptionsContext.Provider>
              </MarkdownRendererContext.Provider>
            </NodeRegistryContext.Provider>
          </InteractionBusContext.Provider>
        </WorkflowLoopReplayProvider>
      </WorkflowScaleContext.Provider>
    </WorkflowSessionStoreContext.Provider>
  );
}
